import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import {
    MultiZoomContextState,
    useMultipleZoomContext,
} from 'utils/components/Zoom/ZoomContext';
import {
    buildPnlExtent,
    buildPnlTickFormat,
} from 'widgets/outliers/components/chart/series/pnl';
import { useContext, useMemo } from 'react';
import AppContext from 'app/AppContext';
import { toBpsFormat } from 'utils/components/TimeSeriesChart/yAxis';
import { buildBenchmarkPerformanceExtent } from 'widgets/outliers/components/chart/series/benchmarkPerformance';
import {
    OutliersData,
    OutliersDataItem,
} from 'widgets/outliers/data/outliersData';
import { OutliersChartSeries } from 'widgets/outliers/components/chart/series/outliersChartSeries';
import { Dimensions } from 'utils/types/Dimensions';
import { NumberFormat } from '@instinet-analytics/core-lib';

interface OutlierAxisStrategy {
    accessor: (d: OutliersDataItem) => number | undefined;
    getTickFormat(
        flipAxis: boolean | undefined,
        multiZoomContext: MultiZoomContextState,
        numberFormat: NumberFormat
    ): (t: number) => string;
    /*  eslint-disable @typescript-eslint/no-explicit-any */
    getDomain(
        flipAxis: boolean | undefined,
        data: OutliersData,
        chartSeries: OutliersChartSeries,
        plotAreaDimensions: Dimensions
    ): any;
}

const pnlAxisStrategy: OutlierAxisStrategy = {
    accessor: (d: OutliersDataItem) => d.pnl,
    getTickFormat: (flipAxis, multiZoomContext, numberFormat) => {
        const { zoomedXScale, zoomedYScale } = multiZoomContext;
        return flipAxis
            ? buildPnlTickFormat(numberFormat, zoomedYScale.domain())
            : buildPnlTickFormat(numberFormat, zoomedXScale.domain());
    },
    getDomain: (flipAxis, data, chartSeries, plotAreaDimensions) => {
        const pnlExtent = buildPnlExtent(
            data.items,
            chartSeries.bubbleMaxPx,
            plotAreaDimensions.width,
            !flipAxis
        );
        return pnlExtent(data.items);
    },
};

const benchmarkPerformanceAxisStrategy: OutlierAxisStrategy = {
    accessor: (d: OutliersDataItem) => d.benchmarkPerformance,
    getTickFormat: (_f, _m, numberFormat) => toBpsFormat(numberFormat),
    getDomain: (flipAxis, data, chartSeries, plotAreaDimensions) => {
        const benchmarkPerformanceExtent = buildBenchmarkPerformanceExtent(
            data.items,
            chartSeries.bubbleMaxPx,
            plotAreaDimensions.height,
            !!flipAxis
        );
        return benchmarkPerformanceExtent(data.items);
    },
};

const getXAxisStrategy = (flipAxis?: boolean) =>
    flipAxis ? benchmarkPerformanceAxisStrategy : pnlAxisStrategy;

const getYAxisStrategy = (flipAxis?: boolean) =>
    flipAxis ? pnlAxisStrategy : benchmarkPerformanceAxisStrategy;

export function useOutliersTickFormats() {
    const {
        config: { flipAxis },
    } = useOutliersConfigSlice();

    const { numberFormat } = useContext(AppContext);
    const multiZoomContext = useMultipleZoomContext();

    return useMemo(() => {
        const xTickFormat = getXAxisStrategy(flipAxis).getTickFormat(
            flipAxis,
            multiZoomContext,
            numberFormat
        );
        const yTickFormat = getYAxisStrategy(flipAxis).getTickFormat(
            flipAxis,
            multiZoomContext,
            numberFormat
        );

        return { xTickFormat, yTickFormat };
    }, [flipAxis, multiZoomContext, numberFormat]);
}

export function useOutliersAxisDomains(
    data: OutliersData | undefined,
    chartSeries: OutliersChartSeries,
    plotAreaDimensions: Dimensions
) {
    const {
        config: { flipAxis },
    } = useOutliersConfigSlice();

    return useMemo(() => {
        const xDomain = data
            ? getXAxisStrategy(flipAxis).getDomain(
                  flipAxis,
                  data,
                  chartSeries,
                  plotAreaDimensions
              )
            : [0, 1];
        const yDomain = data
            ? getYAxisStrategy(flipAxis).getDomain(
                  flipAxis,
                  data,
                  chartSeries,
                  plotAreaDimensions
              )
            : [0, 1];

        return { xDomain, yDomain };
    }, [data, chartSeries, plotAreaDimensions, flipAxis]);
}

export function getOutliersAccessors(flipAxis: boolean | undefined) {
    const xAccessor = getXAxisStrategy(flipAxis).accessor;
    const yAccessor = getYAxisStrategy(flipAxis).accessor;
    return { xAccessor, yAccessor };
}
